import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Page, Field } from "../components/Primitives";
import { toast } from "sonner";
import { Plus, X, Trash2, KeyRound, ShieldCheck, CheckSquare, Square, Copy, Lock, FileClock, RefreshCw, Filter } from "lucide-react";

const MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "showroom", label: "Showroom" },
  { key: "leads", label: "Leads & CRM" },
  { key: "service", label: "Service Shop" },
  { key: "stores", label: "Stores" },
  { key: "accounting", label: "Accounting" },
  { key: "warranty", label: "Warranty" },
  { key: "customers", label: "Customers" },
  { key: "vendors", label: "Vendors" },
  { key: "employees", label: "Team & Bays" },
  { key: "reports", label: "Reports" },
  { key: "users", label: "User Management" },
  { key: "settings", label: "Settings" },
];

const emptyPerms = () =>
  MODULES.reduce((acc, m) => ({ ...acc, [m.key]: { view: false, edit: false, delete: false } }), {});

function PermissionMatrix({ perms, onChange, readOnly = false }) {
  const set = (modKey, which, val) =>
    onChange({ ...perms, [modKey]: { ...(perms[modKey] || { view: false, edit: false, delete: false }), [which]: val } });

  const setRow = (modKey, val) => onChange({ ...perms, [modKey]: { view: val, edit: val, delete: val } });
  const setAll = (val) => {
    const next = { ...perms };
    MODULES.forEach((m) => { next[m.key] = { view: val, edit: val, delete: val }; });
    onChange(next);
  };

  return (
    <div>
      {!readOnly && (
        <div className="flex gap-2 mb-3">
          <button type="button" onClick={() => setAll(true)} className="kk-pill kk-pill-muted" data-testid="perm-all-on">
            <CheckSquare size={11} /> All on
          </button>
          <button type="button" onClick={() => setAll(false)} className="kk-pill kk-pill-muted" data-testid="perm-all-off">
            <Square size={11} /> Clear
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="kk-table">
          <thead>
            <tr>
              <th>Module</th><th className="text-center">View</th><th className="text-center">Edit</th><th className="text-center">Delete</th>
              {!readOnly && <th>All</th>}
            </tr>
          </thead>
          <tbody>
            {MODULES.map((m) => {
              const p = perms[m.key] || { view: false, edit: false, delete: false };
              return (
                <tr key={m.key}>
                  <td className="font-semibold">{m.label}</td>
                  {["view", "edit", "delete"].map((w) => (
                    <td key={w} className="text-center">
                      <input
                        type="checkbox"
                        checked={!!p[w]}
                        disabled={readOnly}
                        onChange={(e) => set(m.key, w, e.target.checked)}
                        className="w-4 h-4"
                        data-testid={`perm-${m.key}-${w}`}
                      />
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="text-center">
                      <button type="button" onClick={() => setRow(m.key, !(p.view && p.edit && p.delete))}
                        className="text-xs underline" data-testid={`perm-row-${m.key}`}>
                        {(p.view && p.edit && p.delete) ? "clear" : "grant"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Roles sub-tab ----------
function RolesPanel() {
  const [roles, setRoles] = useState([]);
  const [openEdit, setOpenEdit] = useState(null); // role object or {new:true}
  const [form, setForm] = useState({ name: "", description: "", permissions: emptyPerms() });

  const load = async () => {
    const { data } = await api.get("/roles"); setRoles(data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({ name: "", description: "", permissions: emptyPerms() });
    setOpenEdit({ new: true });
  };
  const startEdit = (r) => {
    const p = { ...emptyPerms(), ...(r.permissions || {}) };
    setForm({ name: r.name, description: r.description || "", permissions: p });
    setOpenEdit(r);
  };
  const startClone = (r) => {
    const p = { ...emptyPerms(), ...(r.permissions || {}) };
    setForm({ name: `${r.name} Copy`, description: r.description || "", permissions: p });
    setOpenEdit({ new: true });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (openEdit.new) {
        await api.post("/roles", form);
        toast.success("Role created");
      } else {
        await api.put(`/roles/${openEdit.id}`, form);
        toast.success("Role updated");
      }
      setOpenEdit(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (r) => {
    if (r.is_system) return toast.error("System roles can't be deleted.");
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await api.delete(`/roles/${r.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kk-overline">Roles</div>
          <h3 className="font-display text-xl uppercase">{roles.length} roles defined</h3>
          <p className="text-xs text-[var(--kk-muted)] mt-1">System roles are read-only. Use "Edit access" on a system role to create a custom role like "Senior Mechanic" with tweaked permissions.</p>
        </div>
        <button className="kk-btn-primary" onClick={startNew} data-testid="add-role"><Plus size={14}/> New Role</button>
      </div>

      <table className="kk-table">
        <thead><tr><th>Name</th><th>Type</th><th>Description</th><th>Modules granted</th><th></th></tr></thead>
        <tbody>
          {roles.map((r) => {
            const granted = Object.values(r.permissions || {}).filter((p) => p.view || p.edit || p.delete).length;
            return (
              <tr key={r.id} data-testid={`role-row-${r.id}`}>
                <td><div className="font-semibold flex items-center gap-2">{r.name} {r.is_system && <Lock size={11} className="text-[var(--kk-muted)]" />}</div></td>
                <td><span className={"kk-pill " + (r.is_system ? "kk-pill-muted" : "kk-pill-lime")}>{r.is_system ? "System" : "Custom"}</span></td>
                <td className="text-xs text-[var(--kk-muted)]">{r.description || "—"}</td>
                <td className="font-mono text-xs">{granted} / {MODULES.length}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="kk-pill kk-pill-muted" onClick={() => startClone(r)} data-testid={`clone-role-${r.id}`}>
                      <Copy size={11}/> Edit access
                    </button>
                    {!r.is_system && (
                      <>
                        <button className="kk-pill kk-pill-muted" onClick={() => startEdit(r)} data-testid={`edit-role-${r.id}`}>Edit</button>
                        <button className="kk-pill kk-pill-muted" onClick={() => remove(r)} data-testid={`del-role-${r.id}`}>
                          <Trash2 size={11}/>
                        </button>
                      </>
                    )}
                    {r.is_system && (
                      <button className="kk-pill kk-pill-muted" onClick={() => startEdit({ ...r, _viewOnly: true })} data-testid={`view-role-${r.id}`}>View</button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {openEdit && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpenEdit(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-[var(--kk-surface)] p-6 md:p-8 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="kk-overline">{openEdit.new ? "New role" : openEdit._viewOnly ? "View role" : "Edit role"}</div>
                <h2 className="font-display text-2xl uppercase">{form.name || "Untitled"}</h2>
              </div>
              <button type="button" onClick={() => setOpenEdit(null)}><X/></button>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-5">
              <Field label="Name *">
                <input className="kk-input" required value={form.name}
                  disabled={openEdit._viewOnly}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" />
              </Field>
              <Field label="Description">
                <input className="kk-input" value={form.description}
                  disabled={openEdit._viewOnly}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </div>

            <div className="mt-6">
              <div className="kk-overline mb-2">Module Permissions</div>
              <PermissionMatrix
                perms={form.permissions}
                onChange={(p) => setForm({ ...form, permissions: p })}
                readOnly={!!openEdit._viewOnly}
              />
            </div>

            {!openEdit._viewOnly && (
              <div className="flex gap-2 mt-6">
                <button type="submit" className="kk-btn-primary" data-testid="save-role">
                  {openEdit.new ? "Create Role" : "Update Role"}
                </button>
                <button type="button" className="kk-btn-ghost" onClick={() => setOpenEdit(null)}>Cancel</button>
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- Users sub-tab ----------
const emptyUser = {
  email: "", password: "", name: "", phone: "",
  user_type: "staff", role: "advisor", role_id: "",
  permission_overrides: {}, active: true,
};

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/roles")]);
    setUsers(u.data); setRoles(r.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm({ ...emptyUser, permission_overrides: {} }); setOpen(true); };
  const startEdit = (u) => {
    setEditing(u.id);
    setForm({
      ...emptyUser, email: u.email, name: u.name, phone: u.phone || "",
      user_type: u.user_type || "staff", role: u.role, role_id: u.role_id || "",
      permission_overrides: u.permission_overrides || {}, active: u.active !== false,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const body = { ...form };
        delete body.email; delete body.password;
        await api.put(`/users/${editing}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { password: newPw });
      toast.success("Password reset");
      setResetFor(null); setNewPw("");
    } catch { toast.error("Failed"); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kk-overline">Users</div>
          <h3 className="font-display text-xl uppercase">{users.length} accounts</h3>
          <p className="text-xs text-[var(--kk-muted)] mt-1">Set email, password, type, role, and module-level overrides for each person.</p>
        </div>
        <button className="kk-btn-primary" onClick={startNew} data-testid="add-user"><Plus size={14}/> New User</button>
      </div>

      <table className="kk-table">
        <thead><tr><th>Name</th><th>Email</th><th>Type</th><th>Role</th><th>Custom Role</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`}>
              <td><div className="font-semibold">{u.name}</div><div className="text-xs text-[var(--kk-muted)]">{u.phone}</div></td>
              <td className="font-mono text-xs">{u.email}</td>
              <td><span className={"kk-pill " + (u.user_type === "admin" ? "kk-pill-black" : "kk-pill-muted")}>{u.user_type || "staff"}</span></td>
              <td className="text-xs capitalize">{u.role}</td>
              <td className="text-xs">{u.role_name || "—"}</td>
              <td>{u.active === false ? <span className="kk-pill kk-pill-muted">disabled</span> : <span className="kk-pill kk-pill-lime">active</span>}</td>
              <td>
                <div className="flex gap-1">
                  <button className="kk-pill kk-pill-muted" onClick={() => startEdit(u)}>Edit</button>
                  <button className="kk-pill kk-pill-muted" onClick={() => { setResetFor(u); setNewPw(""); }} data-testid={`reset-pw-${u.id}`}>
                    <KeyRound size={11}/>
                  </button>
                  <button className="kk-pill kk-pill-muted" onClick={() => remove(u)} data-testid={`del-user-${u.id}`}><Trash2 size={11}/></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {open && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-[var(--kk-surface)] p-6 md:p-8 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="kk-overline">{editing ? "Edit user" : "New user"}</div>
                <h2 className="font-display text-2xl uppercase">{form.name || form.email || "Untitled"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)}><X/></button>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-5">
              <Field label="Name *"><input className="kk-input" required value={form.name} onChange={upd("name")} data-testid="user-name" /></Field>
              <Field label="Phone"><input className="kk-input" value={form.phone} onChange={upd("phone")} /></Field>
              <Field label="Email *"><input className="kk-input" required type="email" value={form.email}
                disabled={!!editing}
                onChange={upd("email")} data-testid="user-email" /></Field>
              {!editing && (
                <Field label="Password *"><input className="kk-input" required type="password" value={form.password}
                  onChange={upd("password")} minLength={6} data-testid="user-password" /></Field>
              )}
              <Field label="User type *">
                <select className="kk-input" value={form.user_type} onChange={upd("user_type")} data-testid="user-type">
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                  <option value="customer">Customer</option>
                </select>
              </Field>
              {form.user_type === "staff" && (
                <Field label="Built-in role">
                  <select className="kk-input" value={form.role} onChange={upd("role")} data-testid="user-role">
                    <option value="advisor">Service Advisor</option>
                    <option value="mechanic">Mechanic</option>
                    <option value="storekeeper">Store Keeper</option>
                    <option value="accountant">Accountant</option>
                  </select>
                </Field>
              )}
              <Field label="Custom role (optional)">
                <select className="kk-input" value={form.role_id || ""} onChange={upd("role_id")}>
                  <option value="">— none —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{`${r.name}${r.is_system ? " (system)" : ""}`}</option>)}
                </select>
                <div className="text-[0.6rem] text-[var(--kk-muted)] mt-1">
                  Custom role overrides the built-in role's defaults. Per-module overrides below override both.
                </div>
              </Field>
              <Field label="Status">
                <select className="kk-input" value={form.active ? "1" : "0"}
                  onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                  <option value="1">Active</option>
                  <option value="0">Disabled</option>
                </select>
              </Field>
            </div>

            <div className="mt-6">
              <div className="kk-overline mb-2 flex items-center gap-2"><ShieldCheck size={12}/> Per-user permission overrides</div>
              <p className="text-xs text-[var(--kk-muted)] mb-3">
                Only the boxes you tick here are applied on top of the role. Leave a module row untouched to inherit from the role.
              </p>
              <PermissionMatrix
                perms={{ ...emptyPerms(), ...(form.permission_overrides || {}) }}
                onChange={(p) => setForm({ ...form, permission_overrides: p })}
              />
            </div>

            <div className="flex gap-2 mt-6">
              <button type="submit" className="kk-btn-primary" data-testid="save-user">
                {editing ? "Update User" : "Create User"}
              </button>
              <button type="button" className="kk-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {resetFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setResetFor(null)}>
          <form onSubmit={doReset} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[var(--kk-surface)] p-6 md:p-8">
            <div className="kk-overline">Reset password</div>
            <h2 className="font-display text-2xl uppercase">{resetFor.email}</h2>
            <Field label="New password *">
              <input className="kk-input" required type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                minLength={6} data-testid="new-pw" autoFocus />
            </Field>
            <div className="flex gap-2 mt-5">
              <button type="submit" className="kk-btn-primary" data-testid="save-pw"><KeyRound size={14}/> Set password</button>
              <button type="button" className="kk-btn-ghost" onClick={() => setResetFor(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- Page wrapper ----------
export default function Users() {
  const [tab, setTab] = useState("users");
  return (
    <Page
      number="13"
      title="User Management"
      subtitle="Accounts, roles and granular module permissions — bend the software to your org chart."
    >
      <div className="kk-tabs mb-8">
        <button onClick={() => setTab("users")} className={"kk-tab " + (tab === "users" ? "kk-tab-active" : "")} data-testid="user-tab-users">Users</button>
        <button onClick={() => setTab("roles")} className={"kk-tab " + (tab === "roles" ? "kk-tab-active" : "")} data-testid="user-tab-roles">Role Management</button>
        <button onClick={() => setTab("audit")} className={"kk-tab " + (tab === "audit" ? "kk-tab-active" : "")} data-testid="user-tab-audit">Audit Log</button>
      </div>
      {tab === "users" && <UsersPanel />}
      {tab === "roles" && <RolesPanel />}
      {tab === "audit" && <AuditLogPanel />}
    </Page>
  );
}


// ---------- Audit Log sub-tab ----------
const ACTION_LABELS = {
  "portal.signup": "Customer signed up",
  "portal.jobcard.approve": "Approved estimate",
  "portal.invoice.partial_pay": "Part-paid invoice",
  "portal.invoice.razorpay_settle": "Paid via Razorpay",
  "portal.test_drive.create": "Booked test drive",
  "portal.test_drive.cancel": "Cancelled test drive",
  "portal.service_request.create": "Requested service",
  "portal.service_request.cancel": "Cancelled request",
  "portal.vehicle.odometer_update": "Updated odometer",
};

function AuditLogPanel() {
  const [logs, setLogs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custFilter, setCustFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (custFilter) params.customer_id = custFilter;
      if (typeFilter) params.entity_type = typeFilter;
      const { data } = await api.get("/audit-logs", { params });
      setLogs(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [custFilter, typeFilter]);

  const fmtAt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const customerName = (cid) => customers.find(c => c.id === cid)?.name || (cid ? cid.slice(0, 8) + "…" : "—");

  return (
    <div data-testid="audit-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="kk-overline">Trail</div>
          <h3 className="font-display text-2xl uppercase mt-1">Portal audit log</h3>
          <p className="text-xs text-[var(--kk-muted)] mt-1">Every mutation customers make via the portal is timestamped, attributed and stored forever.</p>
        </div>
        <button onClick={load} className="kk-pill kk-pill-muted" data-testid="audit-refresh"><RefreshCw size={11}/> Refresh</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div>
          <div className="kk-overline flex items-center gap-1"><Filter size={10}/> Customer</div>
          <select className="kk-input" value={custFilter} onChange={(e) => setCustFilter(e.target.value)} data-testid="audit-customer-filter">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.phone}</option>)}
          </select>
        </div>
        <div>
          <div className="kk-overline flex items-center gap-1"><Filter size={10}/> Entity type</div>
          <select className="kk-input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="audit-entity-filter">
            <option value="">All</option>
            <option value="customer">Customer</option>
            <option value="vehicle">Vehicle</option>
            <option value="job_card">Job card</option>
            <option value="invoice">Invoice</option>
            <option value="warranty">Warranty</option>
            <option value="test_drive">Test drive</option>
            <option value="service_request">Service request</option>
          </select>
        </div>
        <div className="flex items-end">
          <div className="kk-overline text-[var(--kk-muted)]">{loading ? "Loading…" : `${logs.length} entries`}</div>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="text-sm text-[var(--kk-muted)] py-10 text-center border border-dashed border-[var(--kk-border)]" data-testid="audit-empty">
          <FileClock className="mx-auto mb-2" size={22} />
          No audit entries match these filters yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="kk-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Customer</th>
                <th>Target</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{fmtAt(l.at)}</td>
                  <td>
                    <div className="font-semibold text-sm">{ACTION_LABELS[l.action] || l.action.replace(/^portal\./, "").replace(/\./g, " ")}</div>
                    <div className="text-[0.6rem] font-mono text-[var(--kk-muted)]">{l.action}</div>
                  </td>
                  <td className="text-sm truncate max-w-[180px]">{customerName(l.customer_id)}</td>
                  <td className="text-xs font-mono">
                    {l.entity_type && <span className="kk-pill kk-pill-muted text-[0.55rem] mr-1">{l.entity_type}</span>}
                    {l.entity_id && <span className="text-[var(--kk-muted)]">{l.entity_id.slice(0, 8)}</span>}
                  </td>
                  <td className="text-[0.65rem] font-mono text-[var(--kk-muted)] max-w-[280px]">
                    {l.before && l.after ? (
                      <div className="truncate" title={JSON.stringify({ before: l.before, after: l.after })}>
                        {Object.keys(l.after).map((k) => `${k}: ${JSON.stringify(l.before[k])} → ${JSON.stringify(l.after[k])}`).join(" · ")}
                      </div>
                    ) : l.meta ? (
                      <div className="truncate" title={JSON.stringify(l.meta)}>{JSON.stringify(l.meta)}</div>
                    ) : l.after ? (
                      <div className="truncate" title={JSON.stringify(l.after)}>Created</div>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
